import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import axios from 'axios';
import { Alert, Box, Button, Chip, Link, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { AddAlert as AddAlertIcon, TaskAlt as TaskAltIcon } from '@mui/icons-material';
import StandardModal, { useModalLoading } from '../modales/StandardModal';
import SimpleCustomTable from '../table/SimpleCustomTable';
import { SimpleColumn, SimpleRow } from '../table/interface';
import PermissionButton from './PermissionButton';
import EmptyStateMessage from '../layouts/EmptyStateMessage';
import { operationalTimezone } from '../../utils/operationalDateTime';
import { formatCurrency } from '../../utils/format/currency';
import { useT, type TextKey } from '../../i18n';

/**
 * Historial del cliente: cada cambio con quién, cuándo, desde dónde y el antes y el después (registro del API,
 * versión 1.18.0), y los datos anteriores que guardaba la v1. Si tiene cambios por revisar, quien tenga
 * permiso lo marca como revisado; eso cierra sus alertas en el Centro de alertas. Quien hizo los cambios no
 * puede revisarlos (el API lo impide).
 */
interface ClientChangeHistoryModalProps {
  open: boolean;
  onClose: () => void;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
  canManageBackupAlert?: boolean;
  onBackupAlertChange?: (clientId: number, active: boolean) => void;
  clientData?: {
    id?: number;
    fullName?: string | null;
    backupAlertActive?: boolean;
    backups?: any[];
  } | null;
}

type Source = 'create' | 'edit' | 'location' | 'credit_limit';
interface FieldChange {
  field: string;
  before: string | null;
  after: string | null;
}
interface ChangeEntry {
  id: number;
  source: Source;
  changes: FieldChange[];
  sensitive: boolean;
  changedBy: { id: number; username: string | null; fullName: string | null } | null;
  changedFrom: 'web' | 'app';
  createdAt: string;
  alert: { id: number; status: 'open' | 'resolved' | null } | null;
}

/** Mismos datos delicados que el API (src/client-audit/rules.ts). */
const SENSITIVE = new Set([
  'idNumber',
  'fullName',
  'phone',
  'mobilePhone',
  'mainAddress',
  'secondaryAddress',
  'coSignerIdNumber',
  'coSignerPhone',
  'routeId',
  'creditLimit',
  'location',
]);

const formatWhen = (value: string | null | undefined) =>
  value ? moment(value).tz(operationalTimezone()).format('YYYY/MM/DD HH:mm') : '';

/** Enlace a OpenStreetMap para un punto «lat,lng». */
const mapLink = (value: string) => {
  const [lat, lng] = value.split(',');
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=18/${lat}/${lng}`;
};

const ClientChangeHistoryModal: React.FC<ClientChangeHistoryModalProps> = ({
  open,
  onClose,
  showAlert,
  canManageBackupAlert = false,
  onBackupAlertChange,
  clientData,
}) => {
  const t = useT();
  // Cargando propio del modal (el general queda detrás).
  const { loading, showLoading, hideLoading } = useModalLoading();
  const clientId = Number(clientData?.id || 0);
  const backups = useMemo(() => (Array.isArray(clientData?.backups) ? clientData!.backups! : []), [clientData]);
  const [tab, setTab] = useState<'changes' | 'backups'>('changes');
  const [entries, setEntries] = useState<ChangeEntry[]>([]);
  const [routeNames, setRouteNames] = useState<Record<string, string>>({});
  const [openAlerts, setOpenAlerts] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [flagged, setFlagged] = useState(Boolean(clientData?.backupAlertActive));
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    if (!clientId) return;
    showLoading();
    try {
      const { data } = await axios.get<{ data: ChangeEntry[]; openAlerts: number; routeNames?: Record<string, string> }>(
        `clients/${clientId}/changes`,
      );
      setEntries(data.data);
      setOpenAlerts(data.openAlerts);
      setRouteNames(data.routeNames ?? {});
    } catch {
      showAlert(t('clientChanges.loadError'), 'error');
    } finally {
      setLoaded(true);
      hideLoading();
    }
  }, [clientId, hideLoading, showAlert, showLoading, t]);

  useEffect(() => {
    if (!open) return;
    setTab('changes');
    setNote('');
    setLoaded(false);
    setEntries([]);
    setFlagged(Boolean(clientData?.backupAlertActive));
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  const pendingReview = flagged || openAlerts > 0;

  const setReview = async (active: boolean) => {
    if (!clientId) return;
    try {
      showLoading();
      const { data } = await axios.put(`/clients/${clientId}/backup-alert`, { active, note: active ? undefined : note });
      const finalActive = Boolean(data?.client?.backupAlertActive ?? active);
      setFlagged(finalActive);
      onBackupAlertChange?.(clientId, finalActive);
      showAlert(finalActive ? t('clientChanges.markedOk') : t('clientChanges.reviewedOk'), 'success');
      setNote('');
      await load();
    } catch (error) {
      const code = axios.isAxiosError(error) ? (error.response?.data as { code?: string })?.code : undefined;
      showAlert(code === 'SAME_PERSON' ? t('clientChanges.samePerson') : t('clientChanges.updateError'), 'error');
    } finally {
      hideLoading();
    }
  };

  const formatValue = useCallback(
    (field: string, value: string | null): React.ReactNode => {
      if (value === null || value === '') return <Box component="span" sx={{ color: 'text.disabled' }}>{t('clientChanges.empty_value')}</Box>;
      if (field === 'creditLimit') return formatCurrency(Number(value));
      if (field === 'routeId') return routeNames[value] ?? `#${value}`;
      if (field === 'location') {
        return (
          <Link href={mapLink(value)} target="_blank" rel="noopener noreferrer" underline="hover">
            {t('clientChanges.viewMap')}
          </Link>
        );
      }
      return value;
    },
    [routeNames, t],
  );

  const changeColumns: SimpleColumn[] = [
    { field: 'date', headerName: t('clientChanges.colDate'), minWidth: 120, maxWidth: 140 },
    { field: 'who', headerName: t('clientChanges.colWho'), minWidth: 140, width: 170, wrapText: true },
    { field: 'source', headerName: t('clientChanges.colSource'), minWidth: 110, maxWidth: 140, wrapText: true },
    {
      field: 'fieldLabel',
      headerName: t('clientChanges.colField'),
      minWidth: 150,
      wrapText: true,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <span>{row.fieldLabel}</span>
          {row.sensitive && <Chip size="small" color="warning" variant="outlined" label={t('clientChanges.sensitive')} sx={{ height: 20 }} />}
        </Stack>
      ),
    },
    { field: 'before', headerName: t('clientChanges.colBefore'), minWidth: 150, wrapText: true, renderCell: ({ row }) => row.beforeNode },
    { field: 'after', headerName: t('clientChanges.colAfter'), minWidth: 150, wrapText: true, renderCell: ({ row }) => row.afterNode },
    {
      field: 'review',
      headerName: '',
      minWidth: 100,
      maxWidth: 120,
      renderCell: ({ row }) =>
        row.alertStatus ? (
          <Chip
            size="small"
            color={row.alertStatus === 'open' ? 'warning' : 'success'}
            label={row.alertStatus === 'open' ? t('clientChanges.alertOpen') : t('clientChanges.alertResolved')}
            sx={{ height: 20 }}
          />
        ) : null,
    },
  ];

  const changeRows: SimpleRow[] = entries.flatMap((entry) => {
    const who = `${entry.changedBy?.fullName || entry.changedBy?.username || '—'} · ${t(`alerts.from.${entry.changedFrom}` as TextKey)}`;
    const base = {
      date: formatWhen(entry.createdAt),
      who,
      source: t(`clientChanges.source.${entry.source}` as TextKey),
      alertStatus: entry.alert?.status ?? null,
    };
    if (entry.source === 'create') {
      // Al crear no hay «antes»: una sola fila con el nombre y la cédula.
      const name = entry.changes.find((c) => c.field === 'fullName')?.after ?? '';
      const idNumber = entry.changes.find((c) => c.field === 'idNumber')?.after ?? '';
      const summary = [name, idNumber].filter(Boolean).join(' · ');
      return [{ id: `${entry.id}`, ...base, fieldLabel: '—', sensitive: false, before: '', beforeNode: '', after: summary, afterNode: summary }];
    }
    return entry.changes.map((c) => ({
      id: `${entry.id}-${c.field}`,
      ...base,
      fieldLabel: t(`clientChanges.field.${c.field}` as TextKey),
      sensitive: SENSITIVE.has(c.field),
      before: c.before ?? '',
      after: c.after ?? '',
      beforeNode: formatValue(c.field, c.before),
      afterNode: formatValue(c.field, c.after),
    }));
  });

  const backupColumns: SimpleColumn[] = [
    { field: 'idNumber', headerName: t('clientChanges.backupCol.idNumber'), minWidth: 110, maxWidth: 140 },
    { field: 'fullName', headerName: t('clientChanges.backupCol.fullName'), minWidth: 180, width: 200, wrapText: true },
    { field: 'phone', headerName: t('clientChanges.backupCol.phone'), minWidth: 110, maxWidth: 140 },
    { field: 'mobilePhone', headerName: t('clientChanges.backupCol.mobilePhone'), minWidth: 110, maxWidth: 140 },
    { field: 'mainAddress', headerName: t('clientChanges.backupCol.mainAddress'), minWidth: 180, width: 220, wrapText: true },
    { field: 'createdAt', headerName: t('clientChanges.backupCol.createdAt'), minWidth: 140, width: 160 },
  ];

  const backupRows = backups.map((backup, index) => ({
    id: backup.id ?? `${backup.idNumber || 'backup'}-${index}`,
    idNumber: backup.idNumber || '',
    fullName: backup.fullName || '',
    phone: backup.phone || '',
    mobilePhone: backup.mobilePhone || '',
    mainAddress: backup.mainAddress || '',
    createdAt: formatWhen(backup.createdAt),
  }));

  return (
    <StandardModal
      open={open}
      onClose={onClose}
      title={t('clientChanges.title')}
      subtitle={clientData?.fullName ?? undefined}
      maxWidth={1100}
      height={660}
      loading={loading}
      actions={
        <>
          {!pendingReview && canManageBackupAlert ? (
            <Button size="small" startIcon={<AddAlertIcon />} onClick={() => void setReview(true)}>
              {t('clientChanges.markForReview')}
            </Button>
          ) : (
            <span />
          )}
          <Button variant="contained" size="small" onClick={onClose}>
            {t('common.close')}
          </Button>
        </>
      }
    >
      <Stack spacing={1.5} sx={{ minWidth: 0 }}>
        {pendingReview && (
          <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%', minWidth: 0 } }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('clientChanges.pending')}
            </Typography>
            {canManageBackupAlert && <Typography variant="body2">{t('clientChanges.pendingHelp')}</Typography>}
            {canManageBackupAlert && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }} alignItems={{ sm: 'center' }}>
                <TextField
                  size="small"
                  label={t('alerts.note')}
                  placeholder={t('alerts.notePlaceholder')}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  inputProps={{ maxLength: 300 }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <PermissionButton
                  allowed={canManageBackupAlert}
                  deniedTooltip={t('clientChanges.noPermission')}
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<TaskAltIcon />}
                  onClick={() => void setReview(false)}
                  sx={{ flexShrink: 0 }}
                >
                  {t('clientChanges.markReviewed')}
                </PermissionButton>
              </Stack>
            )}
          </Alert>
        )}
        {backups.length > 0 && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab value="changes" label={t('clientChanges.tabChanges')} />
            <Tab value="backups" label={`${t('clientChanges.tabBackups')} (${backups.length})`} />
          </Tabs>
        )}
        {tab === 'changes' ? (
          changeRows.length > 0 ? (
            <SimpleCustomTable
              rows={changeRows}
              columns={changeColumns}
              maxHeight={420}
              rowHeight={24}
              autoRowHeight
              showBorders
              showCount
              exportCsv
              exportFileName="historial_cliente.csv"
              wrapCellContent
            />
          ) : loaded ? (
            <EmptyStateMessage message={t('clientChanges.empty')} align="center" />
          ) : null
        ) : (
          <SimpleCustomTable
            rows={backupRows}
            columns={backupColumns}
            maxHeight={420}
            rowHeight={24}
            autoRowHeight
            showBorders
            showCount
            exportCsv
            exportFileName="datos_anteriores_cliente.csv"
            wrapCellContent
          />
        )}
      </Stack>
    </StandardModal>
  );
};

export default ClientChangeHistoryModal;
